import { useToast } from "@chakra-ui/react";
import { OperationRuleAlertNotificationType } from "enum/OperationRuleAlertNotificationType";
import { StoreState } from "enum/StoreState";
import { getTemplateMessages } from "pages/OceanWeatherIntelligence/InsightAlert/util/getTemplateMessages";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useStores } from "stores/setup/use-store";
import { CreateAlertFormValues } from "../types";

interface UseAlertCreationSubmissionParams {
  onSuccessCallback?: () => void;
  showPhoneVerificationModal?: boolean;
}

export function useAlertCreationSubmission({
  onSuccessCallback,
  showPhoneVerificationModal
}: UseAlertCreationSubmissionParams = {}) {
  const {
    dataStores: { insightAlertStore },
  } = useStores();

  const toast = useToast();
  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });

  const handleSuccess = useCallback(
    (onClose: () => void) => {
      toast({
        title: t("successAlertCreated"),
        status: "success",
        position: "bottom-right",
        isClosable: true,
      });
      onClose();

      if (onSuccessCallback && showPhoneVerificationModal) {
        setTimeout(() => {
          onSuccessCallback();
        }, 500);
      }
    },
    [toast, t, onSuccessCallback, showPhoneVerificationModal]
  );

  const handleErrorState = useCallback(() => {
    toast({
      title: t("errorCreatingAlert"),
      status: "error",
      position: "bottom-right",
      isClosable: true,
    });
  }, [toast, t]);

  const handleCatchError = useCallback(
    (error: unknown) => {
      toast({
        title: t("errorCreatingAlert"),
        description: error instanceof Error ? error.message : undefined,
        status: "error",
        position: "bottom-right",
        isClosable: true,
      });
    },
    [toast, t]
  );

  const handleOnSaveFormAlertModal = useCallback(
    async (value: CreateAlertFormValues, onClose: () => void) => {
      const isInsightAlert = !!value.insightId;

      if (!isInsightAlert && !insightAlertStore.macroRegionOrigin) {
        toast({
          title: t("noMacroRegionOriginWasFound"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const templateMessages = getTemplateMessages();
      const defaultTemplate =
        templateMessages[value?.templateType || "extreme_wind"];

      // Use customTitle/customDescription if provided, otherwise fallback to template
      // If no template exists for this risk type, use empty string (customTitle should always be set by the form)
      const title = value?.customTitle || defaultTemplate?.title || "";
      const description = value?.customDescription || defaultTemplate?.description || "";

      const notificationsObject = {
        id: 0,
        title: title,
        description: description,
        // TODO: Remove minutesType after backend migration
        minutesType: value?.minAdvanceTime,
        minAdvanceTime: value?.minAdvanceTime,
        maxAdvanceTime: value?.maxAdvanceTime,
        minimumEventDuration: value?.minimumEventDuration,
        type: OperationRuleAlertNotificationType?.BEFORE,
        sendUpdates: value?.receiptType?.includes("sendUpdates"),
        sendCancellations: value?.receiptType?.includes("sendCancellations"),
        ...(value?.startTime && value?.endTime && {
          startTime: value.startTime,
          endTime: value.endTime,
        }),
      };

      if (isInsightAlert) {
        // For insight alerts, use the notification title as the alert name
        // Use trim() check to also handle whitespace-only strings
        const alertName = value?.name?.trim() ? value.name : title;

        try {
          await insightAlertStore.addOperationRuleAlertAPI({
            id: 0,
            active: true,
            name: alertName,
            recipients: value.selectedUsers,
            notifications: [notificationsObject],
            operationRuleLocationItemId: value.insightId,
            channels: value?.channels || ["whatsapp", "email", "push_notification"],
            alertType: value?.templateType,
          });

          if (insightAlertStore.state === StoreState.DONE) {
            handleSuccess(onClose);
          } else {
            handleErrorState();
          }
        } catch (error) {
          handleCatchError(error);
        }
        return;
      }

      const selectedGeofence = insightAlertStore.selectedGeofence;
      const geofence = value.geofence?.length ? value.geofence : selectedGeofence;

      let stationId: number | undefined;
      let regionId: number | undefined = value.macroRegionId;
      let pointGeofence: { lat: number; lng: number }[] | undefined;

      const selectedStation = insightAlertStore.selectedStationForAlert;
      const isCustomPoint = selectedStation?.station_id === 0;

      if (isCustomPoint && selectedStation) {
        pointGeofence = [{ lat: selectedStation.station_lat, lng: selectedStation.station_lon }];
        stationId = undefined;
        regionId = undefined;
      } else if (selectedStation && selectedStation.station_id > 0) {
        stationId = selectedStation.station_id;
        regionId = selectedStation.region_id;
      } else if (value.stationRegion) {
        const stationRegionArray = value.stationRegion.split("-");
        if (stationRegionArray.length >= 1 && /^\d+$/.test(stationRegionArray[0])) {
          stationId = parseInt(stationRegionArray[0], 10);
        } else {
          toast({
            title: t("invalidStationRegionFormat"),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      const finalGeofence = pointGeofence || (value?.locationId || stationId ? undefined : geofence);

      // Only send locationId if we don't have stationId or pointGeofence
      // Backend requires only ONE location type: locationId OR geofenceArea OR stationId/regionId
      const finalLocationId = (stationId || pointGeofence) ? undefined : value?.locationId;

      try {
        await insightAlertStore.addOperationRuleAlertAPI({
          id: 0,
          active: true,
          name: value?.name,
          recipients: value.selectedUsers,
          notifications: [notificationsObject],
          geofence: finalGeofence,
          conditionGroups: value.conditionGroups,
          macroRegionOrigin: insightAlertStore.macroRegionOrigin ?? undefined,
          alertType: value?.templateType,
          locationId: finalLocationId,
          channels: value?.channels || ["whatsapp", "email", "push_notification"],
          stationId: stationId,
          regionId: regionId,
        });

        if (insightAlertStore.state === StoreState.DONE) {
          handleSuccess(onClose);
        } else {
          handleErrorState();
        }
      } catch (error) {
        handleCatchError(error);
      }
    },
    [insightAlertStore, t, toast, handleSuccess, handleErrorState, handleCatchError]
  );

  return {
    handleOnSaveFormAlertModal,
    isSubmitting: insightAlertStore.submissionState === StoreState.PEDDING,
  };
}
